import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth.store';
import { GroomingProLogo } from '@/components/layout/GroomingProLogo';
import { LoginPage } from '@/app/routes/login';
import { RestablecerPage } from '@/app/routes/restablecer';
import { PoliticaDatosPage, TerminosCondicionesPage } from '@/app/routes/legal';
import { AppShell } from '@/components/layout/AppShell';
import { PortalShell } from '@/app/portal/PortalShell';
import { PortalLogin } from '@/app/portal/PortalLogin';
import { RequiereModulo } from '@/components/layout/RequiereModulo';
import { SoloEquipoClinico } from '@/components/features/vetcare/SoloEquipoClinico';

/**
 * El resto de las páginas se carga cuando se visita, no al abrir la app.
 *
 * Antes las 21 páginas entraban en un solo archivo de 1,7 MB: quien abría el
 * login descargaba FullCalendar (agenda) y Recharts (gráficas) sin usarlos.
 * En un móvil con datos, eso son varios segundos de pantalla en blanco antes
 * de poder escribir la contraseña.
 *
 * Login, registro y los dos shells quedan estáticos a propósito: son lo
 * primero que se ve, y diferirlos solo añadiría un parpadeo.
 */
const DashboardPage = lazy(() => import('@/app/routes/dashboard').then((m) => ({ default: m.DashboardPage })));
const AgendaPage = lazy(() => import('@/app/routes/agenda').then((m) => ({ default: m.AgendaPage })));
// La presentación pública. Va en su propio trozo: quien entra a trabajar
// todos los días no tiene por qué descargarla.
const BrochurePage = lazy(() => import('@/app/routes/brochure').then((m) => ({ default: m.BrochurePage })));
const CustomersPage = lazy(() => import('@/app/routes/customers').then((m) => ({ default: m.CustomersPage })));
const CustomerDetailPage = lazy(() => import('@/app/routes/customer-detail').then((m) => ({ default: m.CustomerDetailPage })));
const PetsPage = lazy(() => import('@/app/routes/pets').then((m) => ({ default: m.PetsPage })));
const PetDetailPage = lazy(() => import('@/app/routes/pet-detail').then((m) => ({ default: m.PetDetailPage })));
const ServicesPage = lazy(() => import('@/app/routes/services').then((m) => ({ default: m.ServicesPage })));
const ServicesAnalyticsPage = lazy(() => import('@/app/routes/services-analytics').then((m) => ({ default: m.ServicesAnalyticsPage })));
const ReportsPage = lazy(() => import('@/app/routes/reports').then((m) => ({ default: m.ReportsPage })));
const RemindersPage = lazy(() => import('@/app/routes/reminders').then((m) => ({ default: m.RemindersPage })));
const HotelPage = lazy(() => import('@/app/routes/hotel').then((m) => ({ default: m.HotelPage })));
const DaycarePage = lazy(() => import('@/app/routes/daycare').then((m) => ({ default: m.DaycarePage })));
const PosPage = lazy(() => import('@/app/routes/pos').then((m) => ({ default: m.PosPage })));
const ProductsPage = lazy(() => import('@/app/routes/products').then((m) => ({ default: m.ProductsPage })));
const FinancePage = lazy(() => import('@/app/routes/finance').then((m) => ({ default: m.FinancePage })));
const MembershipsPage = lazy(() => import('@/app/routes/stubs').then((m) => ({ default: m.MembershipsPage })));
const PortalPreviewPage = lazy(() => import('@/app/routes/stubs').then((m) => ({ default: m.PortalPreviewPage })));
const StaffPage = lazy(() => import('@/app/routes/staff').then((m) => ({ default: m.StaffPage })));
const InviteAcceptPage = lazy(() => import('@/app/routes/invite-accept').then((m) => ({ default: m.InviteAcceptPage })));
const AccountPage = lazy(() => import('@/app/routes/account').then((m) => ({ default: m.AccountPage })));
const FacturacionPage = lazy(() => import('@/app/routes/facturacion').then((m) => ({ default: m.FacturacionPage })));
const ClinicaPage = lazy(() => import('@/app/routes/clinica').then((m) => ({ default: m.ClinicaPage })));
const ConsultaPage = lazy(() => import('@/app/routes/consulta').then((m) => ({ default: m.ConsultaPage })));
const ImprimirConsultaPage = lazy(() => import('@/app/routes/imprimir-consulta').then((m) => ({ default: m.ImprimirConsultaPage })));
const SettingsPage = lazy(() => import('@/app/routes/settings').then((m) => ({ default: m.SettingsPage })));
const AdminPage = lazy(() => import('@/app/routes/admin').then((m) => ({ default: m.AdminPage })));
const SubscriptionPage = lazy(() => import('@/app/routes/subscription').then((m) => ({ default: m.SubscriptionPage })));
const PortalHome = lazy(() => import('@/app/portal/PortalHome').then((m) => ({ default: m.PortalHome })));
const PortalPetDetail = lazy(() => import('@/app/portal/PortalPetDetail').then((m) => ({ default: m.PortalPetDetail })));
const PortalBookings = lazy(() => import('@/app/portal/PortalBookings').then((m) => ({ default: m.PortalBookings })));
const PortalProfile = lazy(() => import('@/app/portal/PortalProfile').then((m) => ({ default: m.PortalProfile })));
const PortalNotifications = lazy(() => import('@/app/portal/PortalNotifications').then((m) => ({ default: m.PortalNotifications })));

/**
 * Frontera de carga para las páginas diferidas.
 *
 * Va en los guardias y no en cada ruta: los cuatro terminan en <Outlet />, así
 * que envolverlo aquí cubre todas las páginas con una sola indicación de
 * carga. Sin esta frontera, React lanza error al renderizar un lazy que aún
 * no llegó.
 */
function RutaDiferida() {
  return (
    <Suspense fallback={<BootSplash />}>
      <Outlet />
    </Suspense>
  );
}

function BootSplash() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3">
      <GroomingProLogo className="h-10 w-10 animate-pulse" />
      <p className="text-sm text-muted-foreground">Cargando Grooming ERP…</p>
    </div>
  );
}

/** Requires staff user. If a pet-parent lands here, bounces them to /portal.
 *
 *  El superadministrador SÍ pasa: entra al sistema para probarlo, y antes se
 *  le devolvía a /admin en cuanto tocaba cualquier módulo, así que no podía
 *  comprobar si la agenda o el POS funcionaban. */
function RequireStaff() {
  const hydrated = useAuthStore((s) => s.hydrated);
  const session = useAuthStore((s) => s.session);
  const user = useAuthStore((s) => s.user);
  const portal = useAuthStore((s) => s.portal);
  if (!hydrated) return <BootSplash />;
  if (!session) return <Navigate to="/login" replace />;
  if (portal && !user) return <Navigate to="/portal" replace />;
  return <RutaDiferida />;
}

/** Requires SUPERADMIN role. */
function RequireSuperAdmin() {
  const hydrated = useAuthStore((s) => s.hydrated);
  const session = useAuthStore((s) => s.session);
  const user = useAuthStore((s) => s.user);
  if (!hydrated) return <BootSplash />;
  if (!session) return <Navigate to="/login" replace />;
  if (!user?.isSuperAdmin) return <Navigate to="/dashboard" replace />;
  return <RutaDiferida />;
}

/** Requires a pet-parent portal account. */
function RequireParent() {
  const hydrated = useAuthStore((s) => s.hydrated);
  const session = useAuthStore((s) => s.session);
  const portal = useAuthStore((s) => s.portal);
  const user = useAuthStore((s) => s.user);
  if (!hydrated) return <BootSplash />;
  if (!session) return <Navigate to="/portal/login" replace />;
  if (user && !portal) return <Navigate to="/dashboard" replace />;
  return <RutaDiferida />;
}

/**
 * Si el navegador guarda algo que parece una sesión.
 *
 * Sirve para no hacerle esperar a un visitante anónimo: mientras la sesión
 * se hidrata no sabemos quién es, y plantarle una pantalla de carga a quien
 * viene de un enlace de WhatsApp es perder la visita antes de empezar. Si no
 * hay ni rastro de sesión guardada, se le pinta la presentación de una vez.
 *
 * Es una pista, no una comprobación: quien SÍ tenga sesión espera el
 * instante que tarda en hidratarse, y quien mienta en su localStorage solo
 * consigue ver una pantalla de carga de más.
 */
function pareceConSesion(): boolean {
  try {
    return Boolean(window.localStorage.getItem('groomingpro-auth'));
  } catch {
    // Navegación privada o almacenamiento bloqueado: se trata como anónimo.
    return false;
  }
}

/**
 * La raíz del dominio.
 *
 * grooming-erp.com es la puerta del negocio, no la del panel: quien llega
 * sin sesión ve la presentación del producto. Quien ya entró alguna vez va
 * directo a su pantalla — nadie que abre el sistema a trabajar quiere leer
 * un folleto primero.
 */
function Inicio() {
  const hydrated = useAuthStore((s) => s.hydrated);
  const session = useAuthStore((s) => s.session);
  const user = useAuthStore((s) => s.user);
  const portal = useAuthStore((s) => s.portal);

  if (!hydrated) {
    if (!pareceConSesion()) return <RutaDiferida />;
    return <BootSplash />;
  }
  if (!session) return <RutaDiferida />;

  const destino = user?.isSuperAdmin ? '/admin' : portal ? '/portal' : '/dashboard';
  return <Navigate to={destino} replace />;
}

function RedirectIfAuth() {
  const hydrated = useAuthStore((s) => s.hydrated);
  const session = useAuthStore((s) => s.session);
  const user = useAuthStore((s) => s.user);
  const portal = useAuthStore((s) => s.portal);
  if (!hydrated) return <BootSplash />;
  if (session) {
    // A '/dashboard' y no a '/': la raíz es la presentación del producto,
    // y quien acaba de poner su contraseña quiere su panel, no un folleto.
    const target = user?.isSuperAdmin ? '/admin' : portal ? '/portal' : '/dashboard';
    return <Navigate to={target} replace />;
  }
  return <RutaDiferida />;
}

export const router = createBrowserRouter([
  // ─── Public invitation acceptance (no guard — invitee has no session yet) ──
  {
    path: '/invite/:token',
    element: <Suspense fallback={<BootSplash />}><InviteAcceptPage /></Suspense>,
  },

  // ─── Super admin (dueño de la plataforma Grooming ERP) ───────────────
  // Va DENTRO de AppShell: antes colgaba suelta y la pantalla salía sin
  // barra lateral, sin menú y —lo peor— sin botón de cerrar sesión, porque
  // todo eso vive en el Topbar del AppShell. El superadministrador quedaba
  // encerrado en una sola página.
  {
    element: <RequireSuperAdmin />,
    children: [
      {
        element: <AppShell />,
        children: [
          { path: '/admin', element: <AdminPage /> },
          // /account NO va aquí: duplicarla hacía que React Router tomara esta
          // primera, protegida por RequireSuperAdmin, y rebotara al dashboard a
          // todo el que no fuera superadministrador. La de abajo, bajo
          // RequireStaff, sirve para todos —incluido el superadmin, que
          // también pasa ese guard.
        ],
      },
    ],
  },

  // ─── Legales, públicas y sin sesión ────────────────────────────
  // Fuera de RedirectIfAuth a propósito: quien ya entró también tiene que
  // poder abrirlas, y el enlace se le pasa a clientes que no son usuarios.
  // Fuera del layout a propósito: quien llega desde el correo trae una sesión
  // temporal que solo sirve para cambiar la contraseña. Montar el layout
  // dispararía sus consultas y la comprobación de suscripción sin empresa.
  { path: '/restablecer',                 element: <RestablecerPage /> },
  // La raíz decide a quién enseña qué. El folleto también responde en
  // /brochure, para poder enlazarlo sin depender de quién lo abra.
  {
    element: <Inicio />,
    children: [{ path: '/', element: <BrochurePage /> }],
  },
  { path: '/brochure',                   element: <BrochurePage /> },
  { path: '/politica-tratamiento-datos', element: <PoliticaDatosPage /> },
  { path: '/terminos-y-condiciones',     element: <TerminosCondicionesPage /> },

  // ─── Public auth routes ────────────────────────────────────────
  {
    element: <RedirectIfAuth />,
    children: [
      { path: '/login',          element: <LoginPage /> },
      { path: '/portal/login',   element: <PortalLogin /> },
    ],
  },
  // ─── Admin (staff) ─────────────────────────────────────────────
  {
    element: <RequireStaff />,
    children: [
      {
        element: <AppShell />,
        children: [
          { path: '/dashboard', element: <DashboardPage /> },
          { path: '/agenda', element: <AgendaPage /> },
          { path: '/customers', element: <CustomersPage /> },
          { path: '/customers/:id', element: <CustomerDetailPage /> },
          { path: '/pets', element: <PetsPage /> },
          { path: '/pets/:id', element: <PetDetailPage /> },
          { path: '/services', element: <RequiereModulo modulo={['grooming', 'vetcare']}><ServicesPage /></RequiereModulo> },
          { path: '/services/analytics', element: <RequiereModulo modulo={['grooming', 'vetcare']}><ServicesAnalyticsPage /></RequiereModulo> },
          // VetCare: la clínica.
          { path: '/clinica', element: <RequiereModulo modulo="vetcare"><SoloEquipoClinico><ClinicaPage /></SoloEquipoClinico></RequiereModulo> },
          { path: '/clinica/consulta/:id', element: <RequiereModulo modulo="vetcare"><SoloEquipoClinico><ConsultaPage /></SoloEquipoClinico></RequiereModulo> },
          { path: '/reports', element: <RequiereModulo modulo="reports"><ReportsPage /></RequiereModulo> },
          { path: '/reminders', element: <RemindersPage /> },
          { path: '/hotel', element: <RequiereModulo modulo="hotel"><HotelPage /></RequiereModulo> },
          { path: '/daycare', element: <RequiereModulo modulo="daycare"><DaycarePage /></RequiereModulo> },
          { path: '/memberships', element: <RequiereModulo modulo="memberships"><MembershipsPage /></RequiereModulo> },
          { path: '/pos', element: <PosPage /> },
          { path: '/products', element: <ProductsPage /> },
          { path: '/finance', element: <FinancePage /> },
          // Desde el 14/10/2026 cada módulo de pago se exige aquí y en la base
          // (`tiene_modulo`). Agenda, clientes, caja, productos y gastos son el
          // núcleo: los tiene todo el mundo.
          // El guard del módulo va dentro de la página: sin el módulo explica
          // qué es y quién lo enciende, en vez de rebotar sin decir nada. Los
          // avisos de la campanita enlazan aquí.
          { path: '/facturacion', element: <FacturacionPage /> },
          { path: '/staff', element: <StaffPage /> },
          { path: '/portal-preview', element: <RequiereModulo modulo="portal"><PortalPreviewPage /></RequiereModulo> },
          { path: '/subscription', element: <SubscriptionPage /> },
          { path: '/account', element: <AccountPage /> },
          { path: '/settings', element: <SettingsPage /> },
        ],
      },
      // La hoja para imprimir va sin menú ni barra: sale limpia en papel o PDF.
      { path: '/imprimir/consulta/:id', element: <RequiereModulo modulo="vetcare"><SoloEquipoClinico><ImprimirConsultaPage /></SoloEquipoClinico></RequiereModulo> },
    ],
  },
  // ─── Portal (pet parent) ──────────────────────────────────────
  {
    element: <RequireParent />,
    children: [
      {
        path: '/portal',
        element: <PortalShell />,
        children: [
          { index: true, element: <PortalHome /> },
          { path: 'pets/:id', element: <PortalPetDetail /> },
          { path: 'bookings', element: <PortalBookings /> },
          { path: 'notifications', element: <PortalNotifications /> },
          { path: 'profile', element: <PortalProfile /> },
        ],
      },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
]);
